import './styles.scss';

import React, { useMemo } from 'react';

import { useTQConnectConfig } from '../../../../../../hooks/queries/tqConnectQueries';
import { numFormatter } from '../../../../../../shared/formatter';
import { useAppSelector } from '../../../../../../shared/hooks';
import { getCurrentBadge } from '../../../../../../shared/tqConnectUtils';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  type?: 'gamification' | 'tq';
  points: number;
  name?: string;
  icon?: string;
  iconSize?: number | 'sm';
  formatStartLimit?: number;
  borderColor?: React.CSSProperties['borderColor'];
  backgroundColor?: React.CSSProperties['backgroundColor'];
  style?: React.CSSProperties;
  className?: string;
  showTQConnectBadge?: boolean;
}

const PointsPill: React.FC<Props> = ({
  type = 'gamification',
  points,
  icon,
  name = 'XP',
  iconSize = 14,
  style,
  className,
  formatStartLimit = 999,
  borderColor,
  backgroundColor,
  showTQConnectBadge = false,
  ...props
}) => {
  const {
    hostMetadata: { pointsConfig },
  } = useAppSelector((state) => state.app);

  const { data: tqConnectConfigResult } = useTQConnectConfig();

  const tqConnectConfig = tqConnectConfigResult?.tqConnectConfig;

  const currentBadge = getCurrentBadge(tqConnectConfig?.badges || {}, points);

  const size = ((iconSize === 'sm' || !iconSize) && 14) || iconSize;

  const { pointsIconToUse, pointsNameToUse } = useMemo(() => {
    if (type === 'tq') {
      return {
        pointsIconToUse:
          icon ||
          (showTQConnectBadge && (
            <span style={{ fontSize: size }}>{currentBadge?.logo}</span>
          )) ||
          tqConnectConfig?.scoreConfig?.logo,
        pointsNameToUse:
          name ||
          (showTQConnectBadge && currentBadge?.name) ||
          tqConnectConfig?.scoreConfig?.name,
      };
    }

    return {
      pointsIconToUse: icon || pointsConfig?.pointsImage,
      pointsNameToUse: name || pointsConfig?.pointsName,
    };
  }, [
    type,
    icon,
    pointsConfig?.pointsImage,
    pointsConfig?.pointsName,
    name,
    showTQConnectBadge,
    currentBadge?.logo,
    currentBadge?.name,
    size,
    tqConnectConfig?.scoreConfig?.logo,
    tqConnectConfig?.scoreConfig?.name,
  ]);

  return (
    <div
      className={`points-pill ${className} ${iconSize === 'sm' ? 'sm' : ''}`}
      style={{ borderColor, backgroundColor, ...style }}
      {...props}>
      {!pointsIconToUse || typeof pointsIconToUse === 'string' ? (
        <img
          className="points-pill__icon"
          src={
            typeof pointsIconToUse === 'string'
              ? pointsIconToUse
              : pointsConfig?.pointsImage
          }
          alt={`${pointsNameToUse} Icon`}
          width={size}
          height={size}
        />
      ) : (
        pointsIconToUse
      )}
      {showTQConnectBadge
        ? null
        : numFormatter({
            amount: points,
            startLimit: formatStartLimit,
            hideCurrency: true,
            hideTooltip: true,
          })}{' '}
      {pointsNameToUse}
    </div>
  );
};

export default React.memo(PointsPill);
